import { withAccountVerification, withApprovedPayout } from '@hub-la/fe-account-verification'
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Skeleton,
} from '@hub-la/shadcn'
import { Check, CheckCircle2, ChevronDown, CircleDashed, Loader2, X } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ProductStatus } from '../../domain/dtos/product-status'
import { useChangeProductStatus } from '../hooks/use-change-product-status'
import { useClickOutside } from './useClickOutside'

type Props = {
  productId?: string
  status?: ProductStatus
}

const DropdownMenuItemWithApprovedPayout = withApprovedPayout(
  withAccountVerification(DropdownMenuItem, {
    tooltip: 'Você precisa ativar sua conta para ativar vendas',
  }),
)

const ProductIcon = ({ status }: { status?: ProductStatus }) => {
  switch (status) {
    case ProductStatus.SELLING:
    case ProductStatus.PUBLISHED:
      return (
        <div className="bg-green-600 rounded-full w-4 h-4 flex justify-center items-center">
          <Check className="text-primary-foreground w-3 h-3" strokeWidth={3} />
        </div>
      )
    case ProductStatus.WAITLISTED:
      return (
        <div className="bg-amber-600 rounded-full w-4 h-4 flex justify-center items-center">
          <Check className="text-primary-foreground w-3 h-3" strokeWidth={3} />
        </div>
      )
    case ProductStatus.DRAFT:
      return <CircleDashed className="w-4 h-4 text-ring" />
    case ProductStatus.NOT_ENABLED:
    case ProductStatus.NOT_SELLING:
      return (
        <div className="bg-muted-foreground rounded-full w-4 h-4 flex justify-center items-center">
          <X className="text-primary-foreground w-3 h-3" strokeWidth={3} />
        </div>
      )
    case ProductStatus.UNAVAILABLE:
      return (
        <div className="bg-red-700 rounded-full w-4 h-4 flex justify-center items-center">
          <X className="text-primary-foreground w-3 h-3" strokeWidth={3} />
        </div>
      )
    default:
      return <CircleDashed className="w-4 h-4 text-ring" />
  }
}

const Element = {
  [ProductStatus.SELLING]: DropdownMenuItemWithApprovedPayout,
  [ProductStatus.WAITLISTED]: DropdownMenuItem,
  [ProductStatus.NOT_SELLING]: DropdownMenuItem,
}

export const ChangeProductStatus = ({ productId, status }: Props) => {
  const [action, setAction] = useState<ProductStatus>()
  const [isChanging, setIsChanging] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const { t } = useTranslation()
  const { mutateAsync: changeStatus } = useChangeProductStatus()

  const onChangeStatus = async (action: ProductStatus) => {
    setIsChanging(true)
    changeStatus({ productId: productId ?? '', status: action })
      .then(() => {
        setAction(action)
      })
      .finally(() => {
        setIsChanging(false)
      })
  }

  const availableActions = [ProductStatus.SELLING, ProductStatus.WAITLISTED, ProductStatus.NOT_SELLING].filter(
    (it) => it !== status,
  )

  const menuRef = useClickOutside(() => setIsOpen(false))
  return (
    <div ref={menuRef}>
      <DropdownMenu open={isOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="w-full sm:w-min gap-2"
            disabled={status === ProductStatus.UNAVAILABLE || isChanging}
            onClick={() => setIsOpen(true)}
          >
            {isChanging ? <Loader2 className="h-4 w-4 animate-spin" /> : <ProductIcon status={status} />}

            {status ? t(`productStatus.${status}`) : <Skeleton className="w-16 h-4" />}

            <ChevronDown className="h-4 w-4 opacity-50" />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent className="w-48">
          {availableActions.map((action) => {
            const Item = Element[action]
            return (
              <Item key={action} onClick={() => onChangeStatus(action)} onClose={() => setIsOpen(false)}>
                <div className="flex items-center gap-2">
                  <ProductIcon status={action} />

                  <span>{t(`productStatus.${action}`)}</span>
                </div>
              </Item>
            )
          })}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={!!action} onOpenChange={() => setAction(undefined)}>
        <DialogContent>
          <DialogHeader>
            <div className="flex justify-between items-start w-full">
              <CheckCircle2 className="h-10 w-10 mb-2 text-green-600" />
            </div>

            <DialogTitle>{t(`changeStatusModal.${action}.title`)}</DialogTitle>

            <p>{t(`changeStatusModal.${action}.subtitle`)}</p>
          </DialogHeader>

          <DialogFooter>
            <Button onClick={() => setAction(undefined)}>{t(`changeStatusModal.close`)}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
